import Button, { ButtonType } from 'legacy/common/components/button/Button';
import Modal, { ModalType } from 'legacy/common/components/modal/Modal';
import React, { useCallback, useRef, useState } from 'react';
import { MdConfirmationNumber } from 'react-icons/md';
import ManageEntryRow from './ManageEntryRow';
import {
  useAdmitGuestMutation,
  useGetEventEntryGuests
} from 'legacy/events/hooks';
import { DEFAULT_PAGE_LIMIT } from 'legacy/events/service';
import { IoMdBarcode } from 'react-icons/io';
import { GuestEntryListItem } from 'legacy/events/types';
import EntryDetail from './EntryDetail';
import EntryMenu from './EntryMenu';
import { showToast } from 'legacy/common/utils/toast';

interface ManageEntryModalProps {
  eventId: string;
  onClose: () => void;
}

const ManageEntryModal: React.FC<ManageEntryModalProps> = ({
  eventId,
  onClose
}) => {
  const [activeEntry, setActiveEntry] = useState<GuestEntryListItem | null>(
    null
  );
  const [activeThreeDotsEntry, setActiveThreeDotsEntry] =
    useState<GuestEntryListItem | null>(null);
  const admitGuestMutation = useAdmitGuestMutation();

  const onClickAdmit = (entry: GuestEntryListItem) => {
    admitGuestMutation.mutate(
      {
        eventId,
        ticketId: entry.id
      },
      {
        onSuccess: () => {
          showToast({
            type: 'success',
            message: (
              <>
                <span className="font-semibold">
                  {entry?.type === 'complimentary'
                    ? entry?.name
                    : `${entry?.user?.firstName} ${entry?.user?.lastName}`}{' '}
                </span>
                was successfully admitted
              </>
            )
          });
        }
      }
    );
  };

  const [pageNumber, setPageNumber] = useState(0);
  const { data, isFetching, fetchNextPage } = useGetEventEntryGuests({
    eventId
  });
  const ref = useRef<IntersectionObserver>();
  const lastElementRef = useCallback(
    (node: HTMLDivElement) => {
      if (isFetching) {
        return;
      }

      if (ref.current) {
        ref.current.disconnect();
      }

      ref.current = new IntersectionObserver((entries) => {
        if (
          entries[0].isIntersecting &&
          data?.pages?.[data.pages.length - 1]?.items.length >=
            DEFAULT_PAGE_LIMIT
        ) {
          fetchNextPage({
            pageParam: pageNumber + 1
          });
          setPageNumber((pageNumber) => pageNumber + 1);
        }
      });

      if (node) {
        ref.current.observe(node);
      }
    },
    [isFetching, pageNumber]
  );

  const flattenedData = data?.pages?.flatMap((page) => page.items) || [];

  const onClickEntryRow = (entry: GuestEntryListItem) => {
    setActiveEntry(entry);
  };

  const onCloseDetail = () => {
    setActiveEntry(null);
  };

  const onClickScan = () => {
    onClose();
  };

  const onCloseEntryMenu = () => {
    setActiveThreeDotsEntry(null);
  };

  return (
    <>
      <Modal
        visible
        onClose={onClose}
        type={ModalType.SMALL}
        title="Manage Entry"
        icon={<MdConfirmationNumber size={20} />}
        renderFooter={() => (
          <Button
            type={ButtonType.Primary}
            onClick={onClose}
            icon={<IoMdBarcode size={20} />}
          >
            Scan
          </Button>
        )}
        isBehind={!!activeEntry || !!activeThreeDotsEntry}
      >
        <div className="pb-20">
          {flattenedData.map((entry) => (
            <div className="cursor-pointer" ref={lastElementRef} key={entry.id}>
              <ManageEntryRow
                entry={entry}
                onClick={() => onClickEntryRow(entry)}
                onClickThreeDots={() => setActiveThreeDotsEntry(entry)}
              />
            </div>
          ))}
        </div>
      </Modal>

      {activeEntry && (
        <EntryDetail
          eventId={eventId}
          entry={activeEntry}
          onClose={onCloseDetail}
          onClickScan={onClickScan}
        />
      )}

      {activeThreeDotsEntry && (
        <EntryMenu
          entry={activeThreeDotsEntry}
          isAdmitting={admitGuestMutation.isLoading}
          onClose={onCloseEntryMenu}
          onClickViewDetails={(entry) => {
            setActiveThreeDotsEntry(null);
            setActiveEntry(entry);
          }}
          onClickAdmit={onClickAdmit}
        />
      )}
    </>
  );
};

export default ManageEntryModal;
